import { t } from '@lingui/core/macro';
import { formatLongDateTime, formatRelativeTime } from '@luminovo/frontend-utils';
import { styled } from '@mui/material';
import { FileTextIcon, ReplyIcon, TrashIcon } from 'lucide-react';
import React, { type JSX } from 'react';

import { colorSystem } from '../../../theme';
import { AvatarIcon } from '../../AvatarIcon';
import { DestructiveSecondaryIconButton, SecondaryIconButton } from '../../buttons';
import { CircularProgress } from '../../CircularProgress';
import { useConfirmationDialog } from '../../Dialog/hooks/useConfirmationDialog';
import { Flexbox } from '../../Flexbox';
import { Text } from '../../Text';
import { Tooltip } from '../../Tooltip';
import { Comment, MentionComponent } from '../types';
import { extractAttachments, renderMarkup } from '../utils/renderMarkup';

const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp'];

function isImagePath(filePath: string): boolean {
    const lowerPath = filePath.toLowerCase();
    return IMAGE_EXTENSIONS.some((ext) => lowerPath.endsWith(ext));
}

function getFileName(filePath: string): string {
    // filePath is like "hash/filename.ext"
    const parts = filePath.split('/');
    return parts[parts.length - 1] || filePath;
}

export const CommentItem = React.memo(
    ({
        comment,
        onReply,
        onDelete,
        overrides,
        disabled,
        getAttachmentUrl,
    }: {
        comment: Comment;
        onReply: (comment: Comment) => void;
        onEdit: (comment: Comment) => void;
        onDelete: (comment: Comment) => void;
        overrides: { MentionComponent: MentionComponent };
        disabled: boolean;
        getAttachmentUrl?: (filePath: string) => Promise<string>;
    }): JSX.Element => {
        const renderedMarkup = renderMarkup(comment.content, overrides);
        const attachments = extractAttachments(comment.content);
        const { isCurrent } = comment.author;

        const authorNameArray = comment.author.label.split(' ').map((n) => n.trim());
        return (
            <Container>
                <AvatarIcon
                    size="small"
                    user={{ email: '', firstName: authorNameArray[0] ?? '', lastName: authorNameArray[1] ?? '' }}
                />
                <Flexbox
                    gap={4}
                    sx={{
                        flexDirection: 'column',
                    }}
                >
                    <Flexbox
                        gap={8}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <Text
                            variant="body-small-semibold"
                            style={{ flexGrow: 1 }}
                            color={comment.resolvedAt === null ? colorSystem.neutral[8] : colorSystem.neutral[6]}
                        >
                            {comment.author.label}{' '}
                            <Tooltip title={formatLongDateTime(comment.updatedAt)}>
                                <Text
                                    variant="body-small"
                                    style={{ marginLeft: 4, whiteSpace: 'pre' }}
                                    color={colorSystem.neutral[6]}
                                >
                                    {formatRelativeTime(comment.updatedAt)}
                                </Text>
                            </Tooltip>
                        </Text>
                        <Flexbox gap={2}>
                            <Tooltip title={t`Reply`}>
                                <SecondaryIconButton
                                    aria-label={t`Reply`}
                                    onClick={() => onReply(comment)}
                                    disabled={disabled}
                                    className="btn-actions"
                                    size="medium"
                                    style={{ width: 20, height: 20 }}
                                >
                                    <ReplyIcon />
                                </SecondaryIconButton>
                            </Tooltip>
                            {isCurrent && <ButtonDelete onDelete={() => onDelete(comment)} disabled={disabled} />}
                        </Flexbox>
                    </Flexbox>

                    <Text
                        style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                        color={comment.resolvedAt === null ? colorSystem.neutral[7] : colorSystem.neutral[5]}
                    >
                        {renderedMarkup}
                    </Text>

                    {attachments.length > 0 && getAttachmentUrl && (
                        <AttachmentsContainer>
                            {attachments.map((filePath) => (
                                <AttachmentThumbnail
                                    key={filePath}
                                    filePath={filePath}
                                    getAttachmentUrl={getAttachmentUrl}
                                />
                            ))}
                        </AttachmentsContainer>
                    )}
                </Flexbox>
            </Container>
        );
    },
);

function ButtonDelete({ onDelete, disabled }: { onDelete: () => void; disabled: boolean }): JSX.Element {
    const { modal, setOpen } = useConfirmationDialog({
        title: t`Delete comment`,
        description: t`Are you sure you want to delete this comment? This cannot be undone.`,
        onConfirmation: () => onDelete(),
    });
    return (
        <>
            {modal}
            <Tooltip title={t`Delete`}>
                <DestructiveSecondaryIconButton
                    aria-label={t`Delete`}
                    className="btn-actions"
                    size="medium"
                    style={{ width: 20, height: 20 }}
                    onClick={() => setOpen(true)}
                    disabled={disabled}
                >
                    <TrashIcon />
                </DestructiveSecondaryIconButton>
            </Tooltip>
        </>
    );
}

function AttachmentThumbnail({
    filePath,
    getAttachmentUrl,
}: {
    filePath: string;
    getAttachmentUrl: (filePath: string) => Promise<string>;
}): JSX.Element {
    const [url, setUrl] = React.useState<string | null>(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [isExpanded, setIsExpanded] = React.useState(false);
    const isImage = isImagePath(filePath);
    const fileName = getFileName(filePath);

    React.useEffect(() => {
        let cancelled = false;
        // oxlint-disable-next-line react/set-state-in-effect -- SOU-4697: the extra render pass is kept deliberately; deriving this during render or resetting via `key` would change what the user sees
        setIsLoading(true);
        getAttachmentUrl(filePath)
            .then((resolvedUrl) => {
                if (!cancelled) {
                    setUrl(resolvedUrl);
                    setIsLoading(false);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [filePath, getAttachmentUrl]);

    if (isLoading) {
        return (
            <ThumbnailContainer>
                <CircularProgress size="small" />
            </ThumbnailContainer>
        );
    }

    if (!url) {
        return <></>;
    }

    // For images: show thumbnail with click-to-expand
    if (isImage) {
        return (
            <>
                <ThumbnailContainer onClick={() => setIsExpanded(true)}>
                    <img src={url} alt={fileName} />
                </ThumbnailContainer>
                {isExpanded && (
                    <ExpandedImageOverlay onClick={() => setIsExpanded(false)}>
                        <ExpandedImage src={url} alt={fileName} onClick={(e) => e.stopPropagation()} />
                    </ExpandedImageOverlay>
                )}
            </>
        );
    }

    // For non-images: show file icon with download link
    return (
        <Tooltip title={t`Download ${fileName}`}>
            <FileAttachmentContainer href={url} target="_blank" rel="noopener noreferrer" download={fileName}>
                <FileTextIcon size={20} color={colorSystem.neutral[6]} />
                <FileNameText>{fileName}</FileNameText>
            </FileAttachmentContainer>
        </Tooltip>
    );
}

const Container = styled('div')({
    display: 'grid',
    width: '100%',
    alignItems: 'baseline',
    gridTemplateColumns: 'auto 1fr',
    gridColumnGap: 8,
    '& .btn-actions': {
        visibility: 'hidden',
    },
    '&:hover .btn-actions': {
        visibility: 'visible',
    },
});

const AttachmentsContainer = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
});

const ThumbnailContainer = styled('div')({
    width: 64,
    height: 64,
    borderRadius: 4,
    overflow: 'hidden',
    border: `1px solid ${colorSystem.neutral[3]}`,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colorSystem.neutral[1],
    '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    '&:hover': {
        borderColor: colorSystem.primary[5],
    },
});

const ExpandedImageOverlay = styled('div')({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    cursor: 'pointer',
});

const ExpandedImage = styled('img')({
    maxWidth: '90vw',
    maxHeight: '90vh',
    objectFit: 'contain',
    borderRadius: 8,
    cursor: 'default',
});

const FileAttachmentContainer = styled('a')({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '6px 10px',
    borderRadius: 4,
    border: `1px solid ${colorSystem.neutral[3]}`,
    backgroundColor: colorSystem.neutral[1],
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
        borderColor: colorSystem.primary[5],
        backgroundColor: colorSystem.neutral[2],
    },
});

const FileNameText = styled('span')({
    fontSize: 12,
    color: colorSystem.neutral[7],
    maxWidth: 120,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});
